import { Constructor, Unboxed } from '@skylab-shared/types';
import { fillPlaceholders, getPlaceholders, hasDuplicates, isString } from '@skylab-shared/utils';
import { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { ConsoleLogger } from '../../logger';
import { TaggedUrl } from '../../web';
import { BusinessObjectBase, BusinessObjectConfig } from './../../bo';
import { LocalContext } from './../../context';
import { HttpService } from './../../http';
import { ModelBase, ModelMetadataContainer, PropertyMetadata } from './../../model';
import { DeleteResponse, RepositoryResponseCode, RepositorySaveResponse, SaveConflictResponse, SaveForbiddenResponse } from './repository-interface';
import { repositoryResponseHandler, RepositoryResponseHandler } from './repository-response-handler';

interface ApiControllerActions {
    get?: string;
    put?: string;
    post?: string;
    delete?: string;
}

export interface RepositoryConfig<TGetDTO, TUpdateDTO> {
    apiController?: ApiControllerActions & {
        base: string;
        tags?: string[];
    } | string;
    customSaveRequestTimeout?: number;
    boType: Constructor<BusinessObjectBase<ModelBase>>;
    dto?: {
        get: Constructor<Unboxed<TGetDTO>> | null;
        update: Constructor<Unboxed<TUpdateDTO>>;
    }
    responseHandler?: RepositoryResponseHandler;
}

export abstract class RepositoryBase<TBO extends BusinessObjectBase<ModelBase>, TSingleItem extends ModelBase, TGetDTO = any, TUpdateDTO = any> {
    protected readonly config: RepositoryConfig<TGetDTO, TUpdateDTO> & {
        modelType: Constructor<ModelBase>;
        identityProperty: PropertyMetadata;
    };

    protected readonly responseHandler: RepositoryResponseHandler;

    protected readonly logger: ConsoleLogger = new ConsoleLogger(this.constructor.name);

    public constructor(
        protected $http: HttpService
    ) {
        const repositoryConfig = <RepositoryConfig<TGetDTO, TUpdateDTO>>Reflect.getMetadata('skl:data-source', this.constructor);
        const { modelType } = <BusinessObjectConfig<ModelBase>>Reflect.getMetadata('skl:bo', repositoryConfig.boType);
        const { identityPropertyMetadata } = <ModelMetadataContainer>Reflect.getMetadata('skl:model', modelType);

        this.config = {
            modelType,
            identityProperty: identityPropertyMetadata as PropertyMetadata,
            ...repositoryConfig
        };
        this.responseHandler = repositoryConfig.responseHandler ?? repositoryResponseHandler;
    }

    //#region BO

    public async createNewAsync(context: LocalContext): Promise<TBO> {
        if (!this.config.boType)
            throw new Error(`Business object's type not specified.`);

        const childContext = context.createLocalContext(this.config.boType.name);
        try {
            const bo = this.getPreparedBO(childContext);
            await bo.createModelAsync();
            return bo;
        } catch (err) {
            childContext.dispose();
            this.logger.error(err);
            throw err;
        }
    }

    public async loadAsync(id: number | string, context: LocalContext): Promise<TBO>;
    public async loadAsync(properties: { id: number, [key: string]: number | string }, context: LocalContext): Promise<TBO>;
    public async loadAsync(idOrProperties: number | string | { id: number, [key: string]: number | string }, context: LocalContext): Promise<TBO> {
        if (!this.config.boType)
            throw new Error(`Business object's type not specified.`);

        const props = typeof idOrProperties === 'number' ? { id: idOrProperties } : idOrProperties;
        const childContext = context.createLocalContext(this.config.boType.name);
        try {
            const bo = this.getPreparedBO(childContext);
            const dto = this.config?.dto?.get;
            const { data } = await this.$http.get<TGetDTO>(this.getPreparedActionUrl('get', props), dto ? { dto } : {});

            if (data instanceof ModelBase) {
                await bo.attachModelAsync(data);
            } else {
                const model = await this.fromPlainObjectAsync(data);
                await bo.attachModelAsync(model);
            }

            return bo;
        } catch (err) {
            childContext.dispose();
            throw new Error(err instanceof Error ? err.message : 'Unknown error');
        }
    }

    public async saveAsync(bo: TBO): Promise<RepositorySaveResponse> {
        bo.removeServerRuleViolations();

        const update = bo.model.isPersistent;
        const data = await this.preparePlainObjectAsync(<TSingleItem>bo.model, bo.localContext);

        this.logger.info(data, 'serialized model');

        try {
            const $httpConfig: AxiosRequestConfig = {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Access-Control-Allow-Origin': '*',
                    'Cache-Control': 'must-revalidate, no-store, no-cache, private'
                }
            };
            if (this.config.customSaveRequestTimeout) $httpConfig.timeout = this.config.customSaveRequestTimeout;

            const $httpUrl = this.getPreparedActionUrl(update ? 'put' : 'post', bo.asPojso());
            const response = update
                ? await this.$http.put($httpUrl, data, $httpConfig)
                : await this.$http.post($httpUrl, data, $httpConfig);

            const result = this.prepareSaveResponse(response);
            this.setIdentityPropertiesAfterSave(bo, result);

            return result;
        } catch (response) {
            this.logger.error(response);

            const result = this.prepareSaveResponse(response as AxiosResponse);
            if (result.code === RepositoryResponseCode.Conflict) {
                bo.onSaveConflictResponse(result);
            }

            return result;
        }
    }

    //#endregion

public async deleteAsync(id: number | string): Promise<DeleteResponse>;
public async deleteAsync(properties: { id: number | string, [key: string]: number | string }): Promise<DeleteResponse>;
public async deleteAsync(properties: { externalId: string, [key: string]: number | string }): Promise<DeleteResponse>;
public async deleteAsync(idOrProperties: number | string | { [key: string]: number | string }): Promise<DeleteResponse> {
    const props = typeof idOrProperties === 'number' || typeof idOrProperties === 'string'
        ? { id: idOrProperties }
        : idOrProperties;
    return this.sendDeleteRequestAsync(props);
}

    public async findAsync(id: number): Promise<TSingleItem>;
    public async findAsync(properties: { id: number, [key: string]: number | string }): Promise<TSingleItem>;
    public async findAsync(idOrProperties: number | { id: number, [key: string]: number | string } | {[key: string]: number | string}): Promise<TSingleItem> {
        const props = typeof idOrProperties === 'number' ? { id: idOrProperties } : idOrProperties;
        const dto = this.config?.dto?.get;
        const { data } = await this.$http.get<TGetDTO>(this.getPreparedActionUrl('get', props), dto ? { dto } : {});
        return this.fromPlainObjectAsync(data);
    }

    //#region Protected methods

    protected abstract preparePlainObjectAsync(model: TSingleItem, context: LocalContext): Promise<TUpdateDTO>;

    protected abstract createBO(): TBO;

    protected getPreparedBO(context: LocalContext): TBO {
        const bo = this.createBO();
        bo.setContext(context);
        return bo;
    }

    private getPreparedActionUrl(action: keyof ApiControllerActions, properties: Object): string {
        const config = this.config.apiController;

        const url = isString(config)
            ? action === 'post' ? config : `${config}/:id`
            : TaggedUrl.join(`${config?.base || ''}${config?.[action] || ''}`, config?.tags || []);

        const placeholders = getPlaceholders(url);

        if (hasDuplicates(placeholders)) throw new Error(`${this.constructor.name}: endpoint ${url} has duplicated placeholders`);

        const props = properties as Record<string, number | string>;
        placeholders.forEach(placeholder => {
            if (!props[placeholder]) throw new Error(`${this.constructor.name}: property ${placeholder} has no value in model. Cannot fill in ${action} endpoint`);
        });

        return fillPlaceholders(url, props);
    }

    protected async sendRequest<T>(method: Method, url: string, config?: AxiosRequestConfig): Promise<T> {
        const requestConfig: AxiosRequestConfig = {
            method,
            url,
            ...config
        };
        const response = await this.$http.sendRequest(requestConfig);
        return response.data;
    }

    //#endregion

    //#region Abstract methods

    protected abstract fromPlainObjectAsync(item: any): Promise<TSingleItem>;

    //#endregion

    //#region Private methods

    private async sendDeleteRequestAsync(properties: { [key: string]: number | string }): Promise<DeleteResponse> {
        try {
            const response = await this.$http.sendRequest({
                method: 'DELETE',
                url: this.getPreparedActionUrl('delete', properties)
            });

            return this.responseHandler.mapDeleteResponse(response, RepositoryResponseCode.Success, true);
        } catch (error: any) {
            return this.responseHandler.mapDeleteResponse(error, this.convertResponseToResponseCode(error), false);
        }
    }

    protected convertResponseToResponseCode(response: AxiosResponse): RepositoryResponseCode {
        if (200 <= response.status && response.status < 300) return RepositoryResponseCode.Success;
        if (response.status === 400) return RepositoryResponseCode.ValidationError;
        if (response.status === 409) return RepositoryResponseCode.Conflict;
        if (response.status === 412) return RepositoryResponseCode.OutOfDateData;
        if (response.status === 403) return RepositoryResponseCode.Forbidden;
        if (response.status === 404) return RepositoryResponseCode.DataNotFound;
        if (response.status === 429) return RepositoryResponseCode.TooManyRequests;
        return RepositoryResponseCode.ExternalError;
    }

    protected prepareSaveResponse(response: AxiosResponse): RepositorySaveResponse | SaveConflictResponse<unknown> | SaveForbiddenResponse<unknown> {
        return this.responseHandler.mapSaveResponse(response, this.convertResponseToResponseCode(response));
    }

    private setIdentityPropertiesAfterSave(bo: TBO, result: RepositorySaveResponse | SaveConflictResponse<unknown> | SaveForbiddenResponse<unknown>): void {
        const obj = (result as any).object;
        if (obj) {
            Reflect.set(bo.model, 'version', obj.version);

            if ((this.config.identityProperty?.propertyName === 'id') && obj.id) {
                bo.model.set(this.config.identityProperty.propertyName, obj.id);
            } else if ((this.config.identityProperty?.propertyName === 'externalId') && obj.externalId) {
                bo.model.set(this.config.identityProperty.propertyName, obj.externalId);
            }
        }
    }

    //#endregion
}
